import { TextField } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import classNames from 'classnames';
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { EngagementPlan, EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { Button } from 'components/Button/Button';
import { TasksListItem } from 'components/EngagementPlanSelectionDrawer/components/TasksListItem/TasksListItem';
import { AddTaskModal } from 'components/EngagementPlanUpdateDrawer/components/AddTaskModal/AddTaskModal';
import { UpdateImageArea } from 'components/EngagementPlanUpdateDrawer/components/UpdateImageArea/UpdateImageArea';
import { FREQUENCY_SELECT_OPTIONS } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { AddCrossIcon } from 'components/Icons';
import { TextArea } from 'components/TextArea/TextArea';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { isTimeDuringSleep } from 'utils/form';
import { countEngagementPlanTasks } from 'utils/taskUtils';
import { shiftFinish } from 'utils/time';
import { customDateTimePickerStyles, makeCustomInputStyles } from '../../../../customStyles';
import style from './CustomizeSection.scss';

type Props = {
  plan: EngagementPlan;
  setUpdatingPlanData: Dispatch<SetStateAction<EngagementPlan>>;
  setSelectedTask: (task: EngagementPlanTask | null) => void;
};

type TasksTimeOptions = {
  allTasksStartTime: Date | null;
  hasValidTimeFormat: boolean;
};

const MAX_TITLE_LENGTH = 40;

export const CustomizeSection: React.FC<Props> = ({ plan, setUpdatingPlanData, setSelectedTask }) => {
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const customInputStyles = makeCustomInputStyles();
  const [tasksTimeOptions, setTasksTimeOptions] = useState<TasksTimeOptions>({ allTasksStartTime: null, hasValidTimeFormat: true });

  const errors = useMemo(() => {
    return tasksTimeOptions
      ? {
          timeFormat: tasksTimeOptions?.hasValidTimeFormat ? '' : 'Please select a valid time',
          dateTime: !tasksTimeOptions?.allTasksStartTime ? '' : isTimeDuringSleep(tasksTimeOptions.allTasksStartTime) ? 'Time must be between 5AM and 12AM' : ''
        }
      : { timeFormat: '', dateTime: '' };
  }, [tasksTimeOptions]);

  const applyAllTasksStartTime = useCallback(() => {
    setUpdatingPlanData((prev: EngagementPlan) => {
      const newStartTime = format(tasksTimeOptions.allTasksStartTime!, 'HH:mm:ss');

      const newTasksList =
        prev.tasks?.map((task) => ({
          ...task,
          deliveryStartTime: newStartTime,
          deliveryExpirationTime: shiftFinish(task.deliveryStartTime, task.deliveryExpirationTime, newStartTime).newFinish
        })) || [];

      return {
        ...prev,
        tasks: newTasksList
      };
    });

    setTasksTimeOptions({ allTasksStartTime: null, hasValidTimeFormat: true });
  }, [tasksTimeOptions, setUpdatingPlanData, setTasksTimeOptions]);

  const handleAddTask = (newTask: EngagementPlanTask) => {
    setUpdatingPlanData((prev: EngagementPlan) => {
      const updatedTasks = [...(prev.tasks || []), newTask];
      return {
        ...prev,
        tasks: updatedTasks,
        taskCount: countEngagementPlanTasks({ tasks: updatedTasks, durationDays: prev.durationDays } as EngagementPlan).total
      };
    });
  };

  const handleRemoveTask = (taskIndex: number) => {
    setUpdatingPlanData((prev: EngagementPlan) => {
      const updatedTasks = prev.tasks?.filter((_, index) => index !== taskIndex) || [];
      return {
        ...prev,
        tasks: updatedTasks,
        taskCount: countEngagementPlanTasks({ tasks: updatedTasks, durationDays: prev.durationDays } as EngagementPlan).total
      };
    });
  };

  return (
    <div className={style.container}>
      <div className={style.scrollableContent}>
        <Text variant="h5" weight={500}>
          Customize plan
        </Text>
        <div className={style.fieldWrapper}>
          <div className={classNames(style.fieldWrapper, style.fieldSmallGap)}>
            <Text variant="sm" color={Color.GRAY}>
              Title
            </Text>
            <TextField
              name="title"
              variant="outlined"
              value={plan.title}
              onChange={(value) => {
                setUpdatingPlanData((prev: EngagementPlan) => ({
                  ...prev,
                  title: capitalize(value.target.value)
                }));
              }}
              slotProps={{ htmlInput: { maxLength: MAX_TITLE_LENGTH } }}
              className={customInputStyles.input}
              error={!plan.title}
            />
            {!plan.title ? (
              <Text variant="sm" className={style.error}>
                The title is required.
              </Text>
            ) : null}
          </div>

          <div className={classNames(style.fieldWrapper, style.fieldSmallGap)}>
            <Text variant="sm" color={Color.GRAY}>
              Description
            </Text>
            <TextArea
              rows={4}
              value={plan.description}
              onChange={(value) => {
                setUpdatingPlanData((prev: EngagementPlan) => ({
                  ...prev,
                  description: capitalize(value.target.value)
                }));
              }}
            />
          </div>
        </div>

        <UpdateImageArea imageUrl={plan.imageSource} setImageUrl={setUpdatingPlanData} />

        <div className={style.multiConditionLine}>
          <div className={style.fieldWrapper}>
            <Text variant="nlg">Duration</Text>
            <div className={style.inputWrapper}>
              <TextField
                name="duration"
                variant="outlined"
                value={plan.durationDays || 1}
                onChange={(value) => {
                  const number = Number(value.target.value);
                  if (number > 0 && number <= 99) {
                    setUpdatingPlanData((prev: EngagementPlan) => {
                      const newTasksList =
                        prev.tasks?.map((task) => {
                          if (task.frequencyType === FREQUENCY_SELECT_OPTIONS.every_day) {
                            return { ...task, endsAfterOccurrences: number };
                          }
                          return task;
                        }) || [];

                      return {
                        ...prev,
                        durationDays: number,
                        tasks: newTasksList,
                        taskCount: countEngagementPlanTasks({ tasks: newTasksList, durationDays: number } as EngagementPlan).total
                      };
                    });
                  }
                }}
                type="number"
                className={classNames(customInputStyles.input, style.numberInput)}
              />
              <Text variant="md" className={style.endsInputLabel}>
                Days
              </Text>
            </div>
          </div>

          <div className={style.startTimeWrapper}>
            <div className={style.fieldWrapper}>
              <Text variant="nlg" className={style.conditionTitle}>
                Task start time
              </Text>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  value={tasksTimeOptions.allTasksStartTime}
                  onChange={(date) => {
                    if (date?.toString() === 'Invalid Date') {
                      setTasksTimeOptions((prev) => ({
                        ...prev!,
                        hasValidTimeFormat: false
                      }));
                      return;
                    }

                    setTasksTimeOptions({
                      allTasksStartTime: date,
                      hasValidTimeFormat: true
                    });
                  }}
                  shouldDisableTime={(timeValue) => isTimeDuringSleep(timeValue)}
                  sx={customDateTimePickerStyles}
                />
              </LocalizationProvider>
              {!!errors.timeFormat && (
                <Text variant="sm" className={style.error}>
                  {errors.timeFormat}
                </Text>
              )}
              {!errors.timeFormat && !!errors.dateTime && (
                <Text variant="sm" className={style.error}>
                  {errors.dateTime}
                </Text>
              )}
            </div>
            <Button
              variant="contained"
              onClick={applyAllTasksStartTime}
              className={style.applyButton}
              disabled={!tasksTimeOptions.allTasksStartTime || !!errors.dateTime || !!errors.timeFormat}
            >
              Apply
            </Button>
          </div>
        </div>

        <div className={style.fieldWrapper}>
          <div className={style.tasksHeader}>
            <Text variant="nlg">Tasks</Text>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCrossIcon width={16} height={16} color={Color.WHITE} backgroundColor={Color.DARK_BLUE_NEW} />}
              onClick={() => setShowAddTaskModal(true)}
              className={style.addTaskButton}
            >
              Add Task
            </Button>
          </div>
          {plan.tasks?.length ? (
            <div className={style.tasksListContainer}>
              {plan.tasks.map((task, index) => (
                <TasksListItem
                  key={index}
                  task={task}
                  duration={plan.durationDays}
                  onClick={() => setSelectedTask({ ...task })}
                  onRemove={() => handleRemoveTask(index)}
                  showRemoveButton={true}
                />
              ))}
            </div>
          ) : (
            <div className={style.emptyTasksMessage}>
              <Text variant="md" color={Color.GRAY}>
                No tasks added yet. Click "Add Task" to create your first task.
              </Text>
            </div>
          )}
        </div>

        <AddTaskModal open={showAddTaskModal} onClose={() => setShowAddTaskModal(false)} onSave={handleAddTask} duration={plan.durationDays} />
      </div>
    </div>
  );
};
